/**
 * W-1 · Coquille de l'application.
 *
 * Trois responsabilités, et rien d'autre : la garde d'accès, la navigation
 * entre les six pages, et la période d'observation partagée. Chaque page reçoit
 * la même `Range` — deux pages qui couvriraient des fenêtres différentes
 * feraient dire à l'opérateur deux choses contradictoires sur le même service.
 *
 * Depuis O-3, la garde n'est plus un jeton saisi mais une SESSION : l'état de
 * connexion se demande au serveur (`GET /auth/me`) au lieu de se déduire d'un
 * stockage local. Un seul endroit sait, et c'est celui qui décide.
 *
 * Passe bento (2026-08-24) : la coquille gagne une BARRE HAUTE. L'identité de
 * l'opérateur y remonte, parce qu'elle était en bas d'une colonne où personne
 * ne regarde ; le pied de la barre latérale ne garde que la santé de l'API,
 * qui est une information de fond et non une information d'action.
 */

import { useCallback, useEffect, useState } from 'react';
import { LoginGate } from './components/LoginGate';
import { RangePicker } from './components/RangePicker';
import { Badge } from './components/ui';
import { ROLE_LABELS, UNAUTHENTICATED_EVENT, api } from './lib/api';
import type { Permission, SessionInfo } from './lib/api';
import { SessionProvider } from './lib/session';
import { presetRange } from './lib/range';
import type { Range } from './lib/range';
import { useApi, useHashRoute } from './lib/useApi';
import { formatDuration } from './lib/format';
import { OverviewPage } from './pages/Overview';
import { UsersPage } from './pages/Users';
import { UsagePage } from './pages/Usage';
import { NetworkPage } from './pages/Network';
import { IncidentsPage } from './pages/Incidents';
import { DeparturesPage } from './pages/Departures';
import { AnnouncementsPage } from './pages/Announcements';
import { AccountsPage } from './pages/Accounts';
import { ReportsPage } from './pages/Reports';
import { AlertsBanner, AlertsDot } from './components/Alerts';
import { Toaster } from './components/Toast';
import { Activity, FileText, Flag, KeyRound, LayoutDashboard, Megaphone, Plane, Radio, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface PageDef {
  route: string;
  label: string;
  /** Icône Lucide (2026-10-14, bloc 1) : un vrai pictogramme, le même jeu que l'app. */
  icon: LucideIcon;
  title: string;
  /** Sous-titre : ce que la page répond, en une phrase. */
  lead: string;
  /** Une page sans période (les rapports portent la leur). */
  scoped: boolean;
  /** Intertitre de la barre latérale. Les pages se lisent par paquets : ce que
   *  font les athlètes d'un côté, l'état du service de l'autre. */
  group: 'Suivi' | 'Exploitation';
  /** Page réservée : absente de la barre pour qui n'a pas cette permission. */
  permission?: Permission;
}

const PAGES: PageDef[] = [
  {
    route: 'overview',
    label: "Vue d'ensemble",
    icon: LayoutDashboard,
    title: "Vue d'ensemble",
    lead: "Combien d’athlètes utilisent l’application, et à quel rythme.",
    scoped: true,
    group: 'Suivi',
  },
  {
    route: 'users',
    label: 'Utilisateurs',
    icon: Users,
    title: 'Utilisateurs',
    lead: "Qui utilise l’application, qui revient, et avec quels téléphones. Aucun athlète n’est identifiable : les numéros d’accréditation ne sont jamais conservés en clair.",
    scoped: true,
    group: 'Suivi',
  },
  {
    route: 'usage',
    label: 'Usage',
    icon: Activity,
    title: 'Usage',
    lead: "Ce que les athlètes consultent vraiment dans l’application, et à quelle étape ils abandonnent.",
    scoped: true,
    group: 'Suivi',
  },
  {
    route: 'network',
    label: 'Réseau',
    icon: Radio,
    title: 'Réseau',
    lead: "Bus qui communiquent leur position, ancienneté de ces positions, et état de la source qui alimente la carte des athlètes.",
    scoped: true,
    group: 'Exploitation',
  },
  {
    route: 'incidents',
    label: 'Incidents',
    icon: Flag,
    title: 'Incidents',
    lead: 'Signalements envoyés par les athlètes depuis l’application, ou saisis directement par le TROC.',
    scoped: true,
    group: 'Exploitation',
  },
  {
    route: 'departures',
    label: 'Départs',
    icon: Plane,
    title: 'Départs',
    lead: "Le plan de transfert vers l'aéroport, tel que l'équipe transport l'envoie chaque jour. Vous le déposez, vous vérifiez ce que l'application en a compris, vous publiez. Les accrédités le voient dans l'application dès la publication.",
    scoped: false,
    group: 'Exploitation',
  },
  {
    route: 'announcements',
    label: 'Annonces',
    icon: Megaphone,
    title: 'Annonces',
    lead: "Un message qui s'affiche sur les téléphones des accrédités : une ligne déviée, un horaire qui change, une consigne du jour. Vous l'écrivez en français et en anglais, vous vérifiez à qui il partira, puis vous l'envoyez.",
    scoped: false,
    group: 'Exploitation',
  },
  {
    route: 'reports',
    label: 'Rapports',
    icon: FileText,
    title: 'Rapports',
    lead: 'Bilans journaliers prêts à être envoyés, à lire ici ou à télécharger.',
    scoped: false,
    group: 'Exploitation',
  },
  {
    route: 'accounts',
    label: 'Comptes',
    icon: KeyRound,
    title: 'Comptes',
    lead: "Qui peut se connecter à ce tableau de bord, et avec quels droits. Un compte par personne : chaque action est signée de son nom.",
    scoped: false,
    group: 'Exploitation',
    permission: 'operators:manage',
  },
];

const GROUPS: PageDef['group'][] = ['Suivi', 'Exploitation'];

/** Santé de l'API : sondée en continu pour que le pied de barre dise la vérité. */
function HealthFoot(): React.ReactElement {
  const health = useApi((s) => api.health(s), 'health', { refreshMs: 30_000 });
  if (health.error) {
    return (
      <span className="row">
        <Badge tone="bad">Service arrêté</Badge>
      </span>
    );
  }
  if (!health.data) {
    return (
      <span className="row">
        <Badge>Sondage…</Badge>
      </span>
    );
  }
  // `base up` / `lenz down` était un état de machine, pas une phrase. L'opérateur
  // doit savoir si le tableau de bord dit vrai — pas quel service porte quel nom.
  const lenzTone = health.data.lenz === 'up' ? 'ok' : health.data.lenz === 'degraded' ? 'warn' : 'bad';
  const lenzLabel =
    health.data.lenz === 'up'
      ? 'Positions des bus reçues'
      : health.data.lenz === 'degraded'
        ? 'Positions des bus irrégulières'
        : 'Positions des bus indisponibles';
  const dbOk = health.data.db === 'up';
  // La version et le temps de marche n'intéressent que l'équipe technique :
  // ils passent en infobulle, l'opérateur ne lit que « ça marche / ça ne marche pas ».
  return (
    <div className="stack" style={{ gap: 6 }} title={`Version ${health.data.version} · en service depuis ${formatDuration(health.data.uptimeSec)}`}>
      <span className="row">
        <Badge tone={dbOk ? 'ok' : 'bad'}>{dbOk ? 'Service en marche' : 'Service arrêté'}</Badge>
        <Badge tone={lenzTone}>{lenzLabel}</Badge>
      </span>
    </div>
  );
}

/** Initiales de l'opérateur — deux lettres suffisent à savoir qui est connecté,
 *  et aucune photo n'a à exister pour un compte de service. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

export function App(): React.ReactElement {
  const [session, setSession] = useState<SessionInfo | null>(null);
  /** Tant que `/auth/me` n'a pas répondu, on ne sait pas : on n'affiche ni la
   *  connexion ni le tableau de bord, sinon l'écran de connexion clignoterait
   *  à chaque rechargement d'une session parfaitement valide. */
  const [booting, setBooting] = useState(true);
  const [range, setRange] = useState<Range>(() => presetRange('24h'));
  const route = useHashRoute('overview');

  useEffect(() => {
    let alive = true;
    api
      .me()
      .then((found) => {
        if (alive) setSession(found);
      })
      .catch(() => {
        // 401 attendu quand personne n'est connecté : ce n'est pas une erreur
        // à afficher, c'est la réponse à la question posée.
        if (alive) setSession(null);
      })
      .finally(() => {
        if (alive) setBooting(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Un 401 peut tomber n'importe quand (session expirée en pleine garde, compte
  // révoqué depuis un autre poste). `lib/api.ts` émet alors cet événement et on
  // repasse à l'écran de connexion — sans recharger la page, donc sans perdre
  // la période sélectionnée.
  useEffect(() => {
    const onLost = (): void => setSession(null);
    window.addEventListener(UNAUTHENTICATED_EVENT, onLost);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, onLost);
  }, []);

  const signOut = useCallback(() => {
    // L'écran bascule sans attendre la réponse : la déconnexion doit paraître
    // immédiate, et le serveur révoque de son côté quoi qu'il arrive.
    setSession(null);
    void api.logout().catch(() => undefined);
  }, []);

  if (booting) {
    return (
      <div className="gate">
        <div className="state state-loading" role="status">
          <span className="state-title">Ouverture de la session…</span>
        </div>
      </div>
    );
  }

  if (session === null) return <LoginGate onSuccess={setSession} />;

  const page = PAGES.find((p) => p.route === route) ?? PAGES[0];
  if (!page) throw new Error('Aucune page déclarée');

  return (
    <SessionProvider session={session}>
      <div className="shell">
        <aside className="sidebar">
          <div className="brand">
            <strong>YOG MOVE</strong>
            <span>Back-office · Dakar 2026</span>
          </div>

          <nav className="nav-groups" aria-label="Sections du tableau de bord">
            {GROUPS.map((group) => (
              <div key={group}>
                <div className="nav-title">{group}</div>
                <div className="nav">
                  {PAGES.filter((p) => p.group === group && (p.permission === undefined || session.permissions.includes(p.permission))).map((p) => (
                    <a
                      key={p.route}
                      href={`#/${p.route}`}
                      aria-current={p.route === page.route ? 'page' : undefined}
                    >
                      <p.icon className="nav-glyph" size={17} strokeWidth={1.9} aria-hidden="true" />
                      {p.label}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Le pied ne garde que la santé du service : une information qu'on
              surveille du coin de l'œil, pas une commande. L'identité de
              l'opérateur est remontée dans la barre haute. */}
          <div className="sidebar-foot">
            <HealthFoot />
          </div>
        </aside>

        <main className="main">
          <div className="topbar">
            {/* Pas de champ de recherche tant qu'il ne filtre rien : une commande qui
                ne fait rien s'essaie une fois et décrédibilise les autres (2026-10-14). */}

            <div className="topbar-actions">
              <a className="icon-btn" href="#/reports" title="Rapports" aria-label="Rapports">
                <FileText size={17} strokeWidth={1.9} aria-hidden="true" />
              </a>
              <AlertsDot />
              <a className="icon-btn" href="#/incidents" title="Signalements" aria-label="Signalements">
                <Flag size={17} strokeWidth={1.9} aria-hidden="true" />
              </a>
              {/* Qui est connecté, et avec quel rôle. Sur un poste partagé de
                  TROC, c'est la seule façon de savoir sous quelle identité on
                  agit avant de cliquer — et donc à qui l'action sera imputée. */}
              <div className="user-chip">
                <span className="avatar" aria-hidden="true">
                  {initials(session.operator.displayName)}
                </span>
                <span className="user-chip-id">
                  <strong>{session.operator.displayName}</strong>
                  <span>
                    {ROLE_LABELS[session.operator.role]} · {session.operator.username}
                  </span>
                </span>
              </div>
              <button type="button" className="btn btn-sm" onClick={signOut}>
                Se déconnecter
              </button>
            </div>
          </div>

          <header className="page-head">
            <div>
              <h1>{page.title}</h1>
              <p>{page.lead}</p>
            </div>
            {page.scoped ? <RangePicker range={range} onChange={setRange} /> : null}
          </header>

          {/*
            Le bandeau d'alerte ne s'affiche que sur la Vue d'ensemble (décision
            du porteur, 2026-10-14 : sur la page Départs, il était la première
            chose lue et n'avait rien à voir avec les départs). Ailleurs, le
            point rouge de la barre haute (AlertsDot) suffit. Il ne rend rien
            quand tout va bien.
          */}
          {page.route === 'overview' ? <AlertsBanner /> : null}

          {page.route === 'overview' ? <OverviewPage range={range} /> : null}
          {page.route === 'users' ? <UsersPage range={range} /> : null}
          {page.route === 'usage' ? <UsagePage range={range} /> : null}
          {page.route === 'network' ? <NetworkPage range={range} /> : null}
          {page.route === 'incidents' ? <IncidentsPage range={range} /> : null}
          {page.route === 'departures' ? <DeparturesPage /> : null}
          {page.route === 'announcements' ? <AnnouncementsPage /> : null}
          {page.route === 'accounts' ? <AccountsPage /> : null}
          <Toaster />
          {page.route === 'reports' ? <ReportsPage /> : null}
        </main>
      </div>
    </SessionProvider>
  );
}
